import { useCallback, useEffect, useRef, useState } from 'react';

import { ChatAuthError, streamWealthChat } from '../api/wealthChatApi';
import type { ChatMessage } from '../types';

const WELCOME: ChatMessage = {
    id: 'welcome',
    role: 'assistant',
    content:
        'Hi! I am your Forge Ultra wealth-building assistant. Ask me about any financial concept — ' +
        'compounding, ETFs, diversification, yield curves, risk — and I will explain it clearly.',
    status: 'done',
    createdAt: Date.now(),
};

const newId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const newSessionId = () => `sess_${newId()}`;

export function useWealthChat() {
    const [messages, setMessages] = useState<ChatMessage[]>([WELCOME]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [sessionId, setSessionId] = useState<string>(newSessionId);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    const patchMessage = useCallback((id: string, patch: (m: ChatMessage) => ChatMessage) => {
        setMessages((prev) => prev.map((m) => (m.id === id ? patch(m) : m)));
    }, []);

    const send = useCallback(
        async (raw: string) => {
            const text = raw.trim();
            if (!text || isStreaming) return;

            const assistantId = newId();
            setMessages((prev) => [
                ...prev,
                { id: newId(), role: 'user', content: text, status: 'done', createdAt: Date.now() },
                { id: assistantId, role: 'assistant', content: '', status: 'streaming', createdAt: Date.now() },
            ]);
            setIsStreaming(true);

            const controller = new AbortController();
            abortRef.current = controller;

            try {
                await streamWealthChat(text, {
                    sessionId,
                    signal: controller.signal,
                    onChunk: (chunk) =>
                        patchMessage(assistantId, (m) => ({ ...m, content: m.content + chunk })),
                });
                patchMessage(assistantId, (m) => ({
                    ...m,
                    status: 'done',
                    content: m.content || 'I could not generate an answer. Please try rephrasing your question.',
                }));
            } catch (err) {
                if (controller.signal.aborted) {
                    patchMessage(assistantId, (m) => ({ ...m, status: 'stopped' }));
                } else {
                    // eslint-disable-next-line no-console
                    console.error('[WealthChat] stream error', err);
                    const message =
                        err instanceof ChatAuthError
                            ? err.message
                            : 'Sorry, I encountered an error connecting to the assistant. Please try again.';
                    patchMessage(assistantId, (m) => ({
                        ...m,
                        status: 'error',
                        content: m.content ? `${m.content}\n\n${message}` : message,
                    }));
                }
            } finally {
                if (abortRef.current === controller) abortRef.current = null;
                setIsStreaming(false);
            }
        },
        [isStreaming, patchMessage, sessionId],
    );

    const stop = useCallback(() => abortRef.current?.abort(), []);

    const reset = useCallback(() => {
        abortRef.current?.abort();
        setMessages([{ ...WELCOME, createdAt: Date.now() }]);
        setSessionId(newSessionId());
    }, []);

    return { messages, isStreaming, send, stop, reset, sessionId };
}
